import { formatCurrency } from '@/lib/format';
import { dayLabel, localDayKey } from './dates';
import { countedAmount, hasReportingAmount, isFlowNeutral, reportingAmount } from './refunds';
import type {
  SortKey,
  SortOrder,
  Transaction,
  TransactionsFilters,
} from './types';

// ── formatting ────────────────────────────────────────────────────────────────

const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>();

export function fmt(n: number, currency = 'BRL'): string {
  const code = currency.trim().toUpperCase() || 'BRL';
  let formatter = CURRENCY_FORMATTERS.get(code);
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      // Intl rejects non-ISO codes (upstream data has carried the raw symbol
      // "R$" as currencyCode); formatCurrency renders those without throwing.
      return formatCurrency(n, { currency: code });
    }
    CURRENCY_FORMATTERS.set(code, formatter);
  }
  return formatter.format(n);
}

// ── filters ───────────────────────────────────────────────────────────────────

export function applyFilters(
  txs: readonly Transaction[],
  filters: TransactionsFilters,
): Transaction[] {
  const search = filters.search.trim().toLowerCase();
  const categorySet = filters.categoryIds.length > 0
    ? new Set(filters.categoryIds)
    : null;

  const out: Transaction[] = [];
  for (const t of txs) {
    if (categorySet && !categorySet.has(t.parentCategoryId ?? t.categoryId)) continue;
    if (filters.accountId && t.accountId !== filters.accountId) continue;
    if (filters.type === 'income' && t.amount <= 0) continue;
    if (filters.type === 'expense' && t.amount >= 0) continue;
    if (filters.paymentMethod && t.paymentType !== filters.paymentMethod) continue;
    if (search) {
      const m = t.merchant.toLowerCase().includes(search);
      const n = t.notes ? t.notes.toLowerCase().includes(search) : false;
      if (!m && !n) continue;
    }
    out.push(t);
  }
  return out;
}

// ── sort ──────────────────────────────────────────────────────────────────────

export function applySort(
  txs: readonly Transaction[],
  sortKey: SortKey,
  order: SortOrder = 'desc',
): Transaction[] {
  const copy = txs.slice();
  // Base comparator always returns "descending" (largest first), then we flip.
  switch (sortKey) {
    case 'date':
      copy.sort((a, b) => b.date.localeCompare(a.date));
      break;
    case 'amount':
      copy.sort((a, b) => {
        const aAmount = reportingAmount(a);
        const bAmount = reportingAmount(b);
        // Rows without a comparable BRL value stay at the end in both directions.
        if (aAmount == null) return bAmount == null ? 0 : 1;
        if (bAmount == null) return -1;
        return order === 'asc' ? aAmount - bAmount : bAmount - aAmount;
      });
      return copy;
    case 'merchant':
      copy.sort((a, b) => b.merchant.localeCompare(a.merchant, 'pt-BR'));
      break;
  }
  if (order === 'asc') copy.reverse();
  return copy;
}

/** The amount color shared by a transaction line and its day header: muted, plain, or green in / red out. */
export function amountColor(value: number, tone: 'signed' | 'neutral', muted = false): string {
  if (muted) return 'text-text-tertiary';
  if (tone === 'neutral') return 'text-text-primary';
  return value >= 0 ? 'text-amount-positive' : 'text-amount-negative';
}

// ── grouping ──────────────────────────────────────────────────────────────────

export interface DaySection<T extends Transaction = Transaction> {
  readonly key: string;
  readonly label: string;
  readonly transactions: readonly T[];
}

export function groupByDay<T extends Transaction>(
  txs: readonly T[],
  sortKey: SortKey,
  now: Date = new Date(),
): DaySection<T>[] {
  if (sortKey !== 'date') {
    return [{ key: '__all__', label: '', transactions: txs }];
  }
  return groupItemsByDay(txs, (t) => localDayKey(t.date)).map(({ key, date, items }) => ({
    key,
    label: dayLabel(date, now),
    transactions: items,
  }));
}

export interface DayGroup<T> {
  /** `yyyy-mm-dd` day the caller derived — the local day for transactions, the UTC day for Espaços. */
  readonly key: string;
  /** Local midnight of that day, what `DayHeader` takes. */
  readonly date: Date;
  readonly items: readonly T[];
}

/**
 * Buckets items by the `yyyy-mm-dd` day `dayKeyOf` derives, newest day first, keeping each day's
 * input order. The caller picks the day semantic so the header agrees with how its rows render
 * the date: `localDayKey` for transactions, the UTC day for Espaços (rendered with `timeZone: 'UTC'`).
 */
export function groupItemsByDay<T>(items: readonly T[], dayKeyOf: (item: T) => string): DayGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = dayKeyOf(item);
    let bucket = groups.get(key);
    if (!bucket) {
      bucket = [];
      groups.set(key, bucket);
    }
    bucket.push(item);
  }
  const keys = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a));
  return keys.map((key) => ({ key, date: new Date(`${key}T00:00:00`), items: groups.get(key)! }));
}

export interface DaySectionWithDate<T extends Transaction = Transaction> extends DaySection<T> {
  /** Local midnight of the section's day; `null` for the ungrouped `__all__` section. */
  readonly date: Date | null;
  /** Signed day total. Cash-flow-neutral transfers count as zero. */
  readonly total: number;
}

export function withDayTotals<T extends Transaction>(
  sections: readonly DaySection<T>[],
): DaySectionWithDate<T>[] {
  return sections.map((s) => {
    if (s.key === '__all__') return { ...s, date: null, total: 0 };
    const total = s.transactions.reduce((acc, t) => acc + countedAmount(t), 0);
    return { ...s, date: new Date(`${s.key}T00:00:00`), total };
  });
}

// ── summary ───────────────────────────────────────────────────────────────────

export interface DailyPoint {
  readonly date: string; // yyyy-mm-dd
  readonly income: number;
  readonly expense: number;
}

export interface Summary {
  readonly count: number;
  readonly income: number;
  readonly expense: number;
  readonly net: number;
  readonly incomeCount: number;
  readonly expenseCount: number;
  /** Per-day income/expense totals across the period the transactions span. */
  readonly dailySeries: readonly DailyPoint[];
}

export function computeSummary(txs: readonly Transaction[]): Summary {
  let income = 0;
  let expense = 0;
  let incomeCount = 0;
  let expenseCount = 0;
  const perDay = new Map<string, { income: number; expense: number }>();
  for (const t of txs) {
    if (isFlowNeutral(t)) continue;
    if (!hasReportingAmount(t)) continue;

    const amount = countedAmount(t);
    const day = localDayKey(t.date);
    const bucket = perDay.get(day) ?? { income: 0, expense: 0 };
    if (amount >= 0) {
      income += amount;
      incomeCount += 1;
      bucket.income += amount;
    } else {
      expense += -amount;
      expenseCount += 1;
      bucket.expense += -amount;
    }
    perDay.set(day, bucket);
  }
  const dailySeries: DailyPoint[] = Array.from(perDay.entries())
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .map(([date, v]) => ({ date, income: v.income, expense: v.expense }));
  return {
    count: txs.length,
    income,
    expense,
    net: income - expense,
    incomeCount,
    expenseCount,
    dailySeries,
  };
}

// ── breakdown ─────────────────────────────────────────────────────────────────

export interface BreakdownSegment {
  readonly categoryId: string;
  readonly total: number;
}

export interface Breakdown {
  readonly segments: readonly BreakdownSegment[];
  readonly total: number;
  readonly byCategoryId: ReadonlyMap<string, number>;
}

export const OTHERS_BUCKET_ID = '__others__';

export function computeBreakdown(
  txs: readonly Transaction[],
  topN = 5,
): Breakdown {
  const byCategoryId = new Map<string, number>();
  let total = 0;
  for (const t of txs) {
    if (isFlowNeutral(t)) continue;
    const amount = countedAmount(t);
    if (amount >= 0) continue; // outflow only
    const v = -amount;
    total += v;
    byCategoryId.set(t.categoryId, (byCategoryId.get(t.categoryId) ?? 0) + v);
  }
  const sorted = Array.from(byCategoryId.entries())
    .sort((a, b) => b[1] - a[1]);

  const top = sorted.slice(0, topN).map(([categoryId, total]) => ({ categoryId, total }));
  const rest = sorted.slice(topN);
  const segments: BreakdownSegment[] = top;
  if (rest.length > 0) {
    const othersTotal = rest.reduce((s, [, v]) => s + v, 0);
    segments.push({ categoryId: OTHERS_BUCKET_ID, total: othersTotal });
  }
  return { segments, total, byCategoryId };
}

// ── amount color (no red on negatives — redesign anti-pattern) ────────────────

/**
 * Returns a tone for an amount. Negatives use a slate ramp (NOT red),
 * positives use an emerald ramp. Intensity scales with `|value| / maxAbs`.
 */
export function getAmountColor(value: number, maxAbs: number): string {
  const ratio = maxAbs > 0 ? Math.min(Math.abs(value) / maxAbs, 1) : 0.5;
  if (value < 0) {
    const l = Math.round(58 - ratio * 28);
    return `hsl(215, 16%, ${l}%)`;
  }
  const l = Math.round(54 - ratio * 22);
  return `hsl(158, 64%, ${l}%)`;
}
